import { auth, clearCustomer, setCustomer } from "@/redux/ducks/customer";
import { ReducersTypes } from "@/redux/reducers/Models";
import { validateCPF, validateCNPJ } from "@/utils/functions";
import { useState, KeyboardEvent, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clearUser } from "@/redux/ducks/user";
import { Link, useNavigate } from "react-router-dom";
import useQuery from "@/hooks/useQuery";
import Icon from '@/atomic/atoms/Icon';
import LanguageSelector from '@/atomic/atoms/LanguageSelector';
import Modal from "../Modal";

const LoginModal = ({
  toggleModalLogin,
}: {
  toggleModalLogin: () => void;
}) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryParams = useQuery();
  const customer = useSelector((state: ReducersTypes) => state.customerReducer);

  const [isLoading, setIsLoading] = useState(false);
  const [isVisiblePassword, setIsVisiblePassword] = useState(false);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [isVisibleResetModal, setIsVisibleResetModal] = useState(false);
  const [password, setPassword] = useState("");
  const [rememberPassword, setRememberPassword] = useState(false);
  const refPassword = useRef<HTMLInputElement>(null);

  const toggleIsVisiblePassword = () => setIsVisiblePassword(prev => !prev);
  const toggleModal = () => setIsVisibleModal(prev => !prev);

  useEffect(() => {
    const webAccessToken = queryParams.get('WebAccessToken') ?? queryParams.get('webAccessToken');
    
    if (webAccessToken && !isLoading) {
      setIsLoading(true);
      dispatch(clearCustomer());
      dispatch(clearUser());
      
      const handleTokenAuth = () => {
        setIsLoading(false);
        window.history.replaceState({}, document.title, window.location.pathname);
      };

      dispatch(auth({ callback: handleTokenAuth, webAccessToken }));
    }
  }, []);

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = value.replace(/\D/g, "");

    let formattedValue = numericValue;
    if (numericValue.length === 11) {
      const cpfWithMask = numericValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      if (validateCPF(cpfWithMask)) {
        formattedValue = cpfWithMask;
      }
    } else if (numericValue.length === 14) {
      const cnpjWithMask = numericValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      if (validateCNPJ(cnpjWithMask)) {
        formattedValue = cnpjWithMask;
      }
    }

    dispatch(setCustomer({ username: formattedValue }));
  };

  const handleDocumentPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    const cleanValue = pastedText.replace(/\D/g, "");

    if (cleanValue.length === 11) {
      const cpfWithMask = cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      if (validateCPF(cpfWithMask)) {
        dispatch(setCustomer({ username: cpfWithMask }));
      }
    } else if (cleanValue.length === 14) {
      const cnpjWithMask = cleanValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      if (validateCNPJ(cnpjWithMask)) {
        dispatch(setCustomer({ username: cnpjWithMask }));
      }
    }
  };

  const onInput = () => {
    setPassword(refPassword.current?.value ?? "");
  };

  const getIsValidAuth = () => {
    const doc = customer?.username ?? "";
    return (validateCPF(doc) || validateCNPJ(doc)) && !!password;
  };

  const goToCreateAccount = () => {
    navigate("/register");
  }

  const handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void = (event) => {
    if (event.key === 'Enter') {
      authLogin();
    }
  };

  const handleAuth = (success: boolean, code?: string) => {
    setIsLoading(false);
    if (!success) {
      if (code === "hasReset") {
        setIsVisibleResetModal(true);
      } else {
        toggleModal();
      }
    }
  }

  const handleResetPassword = () => {
    dispatch(setCustomer({
      forgot: {
        document: customer?.username ?? "",
      }
    }));
    setIsVisibleResetModal(false);
    toggleModalLogin();
    navigate("/forgot/pin");
  }

  const authLogin: (e?: React.FormEvent<HTMLFormElement>) => void = (e) => {
    e?.preventDefault();
    if (isLoading) return;

    const doc = customer?.username ?? "";
    if (!(validateCPF(doc) || validateCNPJ(doc)) || !password) return;

    setIsLoading(true);

    const authPayload: any = {
      username: customer?.username!,
      password,
      callback: handleAuth,
    };

    dispatch(auth(authPayload));
  }

  return (
    <div className="fixed inset-0 z-[1100] flex flex-col bg-brand-dark bg-[url('./assets/home-desk-2x.png')] bg-no-repeat bg-cover bg-top overflow-y-auto">

      {/* Header */}
      <div className="flex items-center w-full h-[75px] md:h-[90px] border-b border-white/10 shrink-0">
        <button
          type="button"
          onClick={toggleModalLogin}
          className="flex items-center justify-center w-[75px] h-[75px] md:w-[90px] md:h-[90px] shrink-0 bg-brand-dark border-none outline-none appearance-none focus:outline-none"
        >
          <img src="/svg/icon-close-white-2.0.svg" alt="Fechar" className="w-[29px] h-[29px]" />
        </button>
        <div className="mx-6">
          <Icon className='dark' width="130" height="63" />
        </div>
        <div className="ml-auto mr-3">
          <LanguageSelector className="text-white" showFlag={true} />
        </div>
      </div>

      {/* Conteúdo */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        <div className="w-full max-w-[333px] md:max-w-[420px] backdrop-blur-[10px] bg-[rgba(105,102,102,0.1)] rounded-[20px] px-5 py-10 flex flex-col gap-5">
          <form className="w-full flex flex-col gap-5" onSubmit={authLogin}>
            <input
              value={customer?.username ?? ""}
              name="username"
              placeholder="Seu CPF/CNPJ"
              className="w-full h-14 rounded-[10px] border border-white bg-white px-4 text-base text-black placeholder:text-[#909090] font-primary"
              onChange={handleDocumentChange}
              onPaste={handleDocumentPaste}
              required
              type="text"
              inputMode="numeric"
              maxLength={18}
            />
            <div className="relative w-full">
              <input
                required
                onInput={onInput}
                type={isVisiblePassword ? "text" : "password"}
                name="password"
                onKeyDown={handleKeyDown}
                placeholder="Sua senha"
                ref={refPassword}
                className="w-full h-14 rounded-[10px] border border-white bg-white px-4 pr-12 text-base text-black placeholder:text-[#909090] font-primary"
              />
              <button type="button" onClick={toggleIsVisiblePassword} className="absolute right-4 top-1/2 -translate-y-1/2">
                <img
                  src={isVisiblePassword ? "/eye-hidden.png" : "/icon-eye.png"}
                  width={24}
                  height={24}
                  alt="Visualizar senha"
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-1 cursor-pointer">
                {/* <input
                  type="checkbox"
                  checked={rememberPassword}
                  onChange={() => setRememberPassword(!rememberPassword)}
                  className="w-5 h-5 accent-white rounded"
                /> */}
                {/* <span className="font-myFont font-normal text-sm text-white leading-[18.5px]">Lembrar senha</span> */}
              </label>
              <Link to="/forgot" onClick={toggleModalLogin} className="font-myFont font-normal text-sm text-white leading-[18.5px]">
                Recuperar senha
              </Link>
            </div>
            <button
              type="submit"
              disabled={!getIsValidAuth() || isLoading}
              className={`w-full h-[60px] rounded-[10px] text-white font-secondary text-base tracking-[-0.4px] transition-colors flex items-center justify-center ${getIsValidAuth() && !isLoading ? "bg-primary hover:opacity-95" : "bg-[#bfbfbf] cursor-not-allowed"}`}
            >
              {isLoading ? (
                <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : "Entrar"}
            </button>
          </form>

          <div className="mt-4 text-center">
            <span className="font-myFont font-normal text-sm text-white leading-[18.5px]">
              Ainda não tem conta?
            </span>
            <br />
            <button type="button" onClick={goToCreateAccount} className="font-secondary font-bold text-sm text-white underline leading-[18.5px] mt-1">
              Crie a sua
            </button>
          </div>
        </div>
      </div>

      {isVisibleModal && <Modal
        title="Atenção!"
        message="CPF/CNPJ ou senha inválidos."
        actionMessage="Fechar"
        action={toggleModal}
        hasCloseButton={false}
      />}

      {isVisibleResetModal && <Modal
        title="Atenção!"
        message="Sua senha expirou, cadastre uma nova senha para continuar"
        actionMessage="Cadastrar"
        action={handleResetPassword}
        actionClassName="w-full bg-brand-dark py-4 rounded-xl text-white text-lg font-bold"
        hasCloseButton={true}
        actionClose={() => setIsVisibleResetModal(false)}
      />}
    </div>
  );
}

export default LoginModal;
